import React from 'react';
import styled from 'styled-components';

/**
 * 一排可点选的按钮，单选。选项少且固定时（学期、星期几、校区、两三种类型）用它代替原生 <select>：
 * 所有选项一眼能看到、一下就能点到，不用展开再滚动。选项多或者会一直增长的，用 SearchableSelect。
 *
 * options: [{ value, label, title?, disabled? }]
 * onChange(value, option)：allowDeselect 时再点一次已选中的按钮，value 为 ''、option 为 null
 */

const ACCENT = '#F1AF59';

const SIZES = {
  md: { height: 36, fontSize: 14, padX: 14, gap: 8 },
  sm: { height: 28, fontSize: 12, padX: 10, gap: 6 },
};

const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${p => SIZES[p.$size].gap}px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${p => SIZES[p.$size].height}px;
  margin: 0;
  padding: 0 ${p => SIZES[p.$size].padX}px;
  border: 1px solid ${p => (p.$active ? ACCENT : '#d1d5db')};
  border-radius: 999px;
  background: ${p => (p.$active ? '#fef3e2' : '#fff')};
  color: ${p => (p.$active ? '#92400e' : '#374151')};
  font-family: inherit;
  font-size: ${p => SIZES[p.$size].fontSize}px;
  font-weight: ${p => (p.$active ? 600 : 500)};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: none;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    border-color: ${ACCENT};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const isEmptyValue = v => v === '' || v === null || v === undefined;

const ChipSelect = ({
  options = [],
  value,
  onChange,
  allowDeselect = false,
  disabled = false,
  size = 'md',
  className,
  style,
  'aria-label': ariaLabel,
}) => {
  const sizeKey = SIZES[size] ? size : 'md';

  return (
    <Group role="group" aria-label={ariaLabel} className={className} style={style} $size={sizeKey}>
      {options.map((option) => {
        const active = !isEmptyValue(value) && String(option.value) === String(value);
        return (
          <Chip
            key={String(option.value)}
            type="button"
            title={option.title}
            aria-pressed={active}
            disabled={disabled || option.disabled}
            $size={sizeKey}
            $active={active}
            onClick={() => {
              if (!active) {
                onChange?.(option.value, option);
              } else if (allowDeselect) {
                onChange?.('', null);
              }
            }}
          >
            {option.label}
          </Chip>
        );
      })}
    </Group>
  );
};

export default ChipSelect;
